import { useState, useCallback } from 'react';
import { MessageOptions } from '@/types/openai';

interface UseAiAssistantStateReturn {
  isLoading: boolean;
  isTyping: boolean;
  setIsLoading: (loading: boolean) => void;
  setIsTyping: (typing: boolean) => void;
  createSendMessageHandler: (handleMessageFlow: (options: MessageOptions) => Promise<void>) => (options: MessageOptions) => Promise<void>;
}

export const useAiAssistantState = (): UseAiAssistantStateReturn => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isTyping, setIsTyping] = useState<boolean>(false);

  const createSendMessageHandler = useCallback((handleMessageFlow: (options: MessageOptions) => Promise<void>) => {
    return async (options: MessageOptions) => {
      console.log('[AI ASSISTANT STATE FINAL] Starting message with proper state coordination:', {
        isStreaming: options.stream !== false,
        currentState: { isLoading, isTyping }
      });

      // Set loading immediately for all requests
      setIsLoading(true);
      
      // Only set typing for streaming requests
      if (options.stream !== false) {
        setIsTyping(true);
        console.log('[AI ASSISTANT STATE FINAL] Set typing=true for streaming request');
      }
      
      try {
        await handleMessageFlow(options);
        console.log('[AI ASSISTANT STATE FINAL] Message flow completed successfully');
        
      } catch (error) {
        console.error('[AI ASSISTANT STATE FINAL] Message flow error:', error);
        // Always reset states on error
        setIsLoading(false);
        setIsTyping(false);
        throw error;
      } finally {
        // For non-streaming, reset immediately
        // For streaming, states will be reset by streaming completion callbacks
        if (options.stream === false) {
          console.log('[AI ASSISTANT STATE FINAL] Non-streaming: resetting states immediately');
          setIsLoading(false);
          setIsTyping(false);
        } else {
          console.log('[AI ASSISTANT STATE FINAL] Streaming: states will be managed by streaming handlers');
          // Only reset loading, keep typing until streaming completes
          setIsLoading(false);
        }
      }
    };
  }, [isLoading, isTyping]);

  return {
    isLoading,
    isTyping,
    setIsLoading,
    setIsTyping,
    createSendMessageHandler
  };
};
